<template>
    <div>
        <div class="d-block d-sm-flex justify-content-sm-between mb-3">
            <h4 class="mt-0 header-title align-self-center">
                <template v-if="section === Section.open">Active</template>
                <template v-if="section === Section.completed">Completed</template>
            </h4>
        </div>
        <b-table
            :fields="tableFields"
            :items="deals.data"
            :thead-class="'deals-thead'"
            bordered
            empty-text="No data."
            hover
            ref="table"
            responsive
            show-empty
            v-loading="deals.loading"
            >
            <template v-slot:cell(created_at)="{ value }">
                {{ value | local_date }}
            </template>
            <template v-slot:cell(vehicle.images)="{ value }">
                <div>
                    <img
                        class="vehicle-image"
                        :src="(value && value.length > 0 && value[0].url) ? value[0].url : '/images/no-image-clean.png'"
                        alt="Vehicle Image"
                        />
                </div>
            </template>
            <template v-slot:cell(vehicle.id)="{ value }">
                FND-{{ value }}
            </template>
            <template v-slot:cell(vehicle)="{ value }">
                <span>{{ getName(value.vehicle_year) }}</span><br/>
                <span>{{ getName(value.vehicle_make) }}</span><br/>
                <span>{{ getName(value.vehicle_model) }}</span>
            </template>
            <template v-slot:cell(vehicle.price)="{ value }">
                {{ value | currency }}
            </template>
            <template v-slot:cell(buy_price)="{ value }">
                {{ value | currency }}
            </template>
            <template v-slot:cell(delivered_date)="{ value }">
                {{ value | local_date }}
            </template>
            <template v-slot:cell(hold_expiry)="{ value }">
                <span
                    v-if="value"
                    :title="$options.filters.local_datetime(value)"
                    v-b-tooltip.hover
                    >
                    {{ value | relative }}
                </span>
            </template>
            <template v-slot:cell(actions)="{ item }">
                <router-link
                    :to="`message/${item.id}/thread/${recipient(item)}`"
                    title="View Message"
                    v-b-tooltip.hover
                    style="cursor: pointer"
                    >
                    <i class="action-deal-icons mdi mdi-email position-relative">
                        <span
                            v-html="getVehicleUnreadMessageCountBubble(item)"
                            class="bubble-count-unread position-absolute"
                            />
                    </i>
                </router-link>
                <label
                    class="action-checkbox"
                    v-if="canSetOfferToPaidOrDelivered(item)"
                    :title="!item.is_paid ? 'Mark as Paid' : 'Unmark as Paid'"
                    v-b-tooltip.hover
                    >
                    <input
                        type="checkbox"
                        :disabled="isItemBusy(item, CustomActions.markPaid)"
                        @change="e => onChangePaid(e.target.checked, item)"
                        :value="item.is_paid"
                        v-model="item.is_paid"
                    />
                    <span>
                        <i
                            class="action-deal-icons mdi"
                            :class="{
                                'mdi-currency-usd-off': !item.is_paid,
                                'mdi-currency-usd text-success': item.is_paid
                            }"
                            />
                    </span>
                    <b-modal
                        :ref="`buyPriceModal-${item.id}`"
                        title="Buy Price"
                        centered
                        >
                        <template>
                            <form @submit.prevent>
                                <b-form-group
                                    :invalid-feedback="buy_price_form.invalidFeedback('amount')"
                                    :state="buy_price_form.state('amount')"
                                    label="Amount"
                                    label-for="buy_price_form"
                                    >
                                    <b-form-input
                                        :state="buy_price_form.state('amount')"
                                        id="buy_price_form"
                                        type="text"
                                        v-model="buy_price_form.amount"
                                        @keypress="handleAmountInput"
                                    />
                                </b-form-group>
                            </form>
                        </template>
                        <div slot="modal-footer">
                            <b-button @click="onCancelBuyPrice(item)">
                                Cancel
                            </b-button>
                            <b-button
                                variant="primary"
                                @click="confirmBuyPrice(item)"
                                >
                                Yes
                            </b-button>
                        </div>
                    </b-modal>
                </label>
                <label
                    class="action-checkbox"
                    v-if="canSetOfferToPaidOrDelivered(item)"
                    :title="!item.is_delivered ? 'Mark as Delivered' : 'Unmark as Delivered'"
                    v-b-tooltip.hover
                    >
                    <input
                        type="checkbox"
                        :disabled="isItemBusy(item, CustomActions.markDelivered)"
                        @change="e => onChangeDelivered(e.target.checked, item)"
                        :value="item.is_delivered"
                        v-model="item.is_delivered"
                    />
                    <span>
                        <i
                            class="action-deal-icons mdi"
                            :class="{
                                'mdi-truck': !item.is_delivered,
                                'mdi-truck-check text-success': item.is_delivered
                            }"
                            />
                    </span>
                </label>
                <label
                    v-if="canArchiveDeal(item)"
                    title="Archive Deal"
                    v-b-tooltip.hover
                    @click="onArchiveDeal(item)"
                    >
                    <span>
                        <i class="action-deal-icons mdi mdi-archive"/>
                    </span>
                </label>
            </template>
        </b-table>
        <pagination
            v-if="deals.meta"
            :pagination="deals.meta"
            @current-change="onPageChange"
            />
    </div>
</template>

<script>
import { groupBy } from 'lodash'
import bootbox from 'bootbox'
import SearchDealInput from "@/components/main/deal/custom/SearchDealInput";
import AuthUtils from "@/utils/auth";
import Routes from "@/constants/routes";
import Deal from "@/models/deal";
import { DealStatus } from "@/constants/deal-statuses";
import { validateOnlyNumber } from "@/utils/validation";
import { POLL_INTERVAL } from "@/constants/intervals";

const CustomActions = {
    markPaid: 1,
    markDelivered: 2,
};

const Section = {
  open: 'open',
  completed: 'completed',
};

export default {
    props: {
        section: {
            type: String,
            default: 'open',
            validator: function (value) {
                return _.values(Section).includes(value)
            }
        }
    },
    components: {
        SearchDealInput
    },
    data() {
        return {
            CustomActions,
            Section,
            deals: {
                data: [],
                meta: null,
                loading: false,
            },
            toggle_paid: true,
            Routes,
            deal_type: 0,
            keyword: null,
            new_messages_count: [],
            buy_price_form: new Form(
                {
                    is_paid: true,
                    amount: null,
                },
                {
                    amount: 'required|numeric|min:0.01|max:999999'
                }
            ),
            pollInterval: null,
            messageCount: 0,
        }
    },
    created() {
        this.getData({});
        if (this.$route.query.deal_type) {
            this.deal_type = Number(this.$route.query.deal_type);
        }
    },
    mounted() {
        this.fetchVehiclesUnreadMessageCount();
        window.Bus.$on('updateVehiclesNewMessageCount', () => {
            this.fetchVehiclesUnreadMessageCount();
        });
        this.startPolling();
    },
    beforeDestroy() {
        this.stopPolling();
    },
    computed: {
        tableFields() {
            let fields = [
                {
                    key: 'created_at',
                    label: 'Date',
                    thStyle: { width: "8%" },
                    class: 'text-center',
                },
                {
                    key: 'vehicle.images',
                    label: 'Vehicle Image',
                    thStyle: { width: "8%" },
                    class: 'text-center',
                },
                {
                    key: 'vehicle.id',
                    label: 'ID',
                    thStyle: { width: "7%" },
                    class: 'text-center',
                },
                {
                    key: 'vehicle',
                    label: 'Year/Make/Model',
                    thStyle: { width: "9%" },
                    class: 'text-center',
                },
                {
                    key: 'vehicle.mileage',
                    label: 'Mileage',
                    thStyle: { width: "8%" },
                    class: 'text-center',
                },
                {
                    key: 'vehicle.company.name',
                    label: 'Dealer',
                    thStyle: { width: "18%" },
                    class: 'text-center',
                },
                {
                    key: 'vehicle.price',
                    label: 'Price',
                    thStyle: { width: "9%" },
                    class: 'text-center',
                },
                {
                    key: 'buy_price',
                    label: 'Deal',
                    visible: this.section === Section.completed,
                    thStyle: { width: "9%" },
                    class: 'text-center',
                },
                {
                    key: 'delivered_date',
                    label: 'Delivery',
                    visible: this.section === Section.completed,
                    thStyle: { width: "9%" },
                    class: 'text-center',
                },
                {
                    key: 'hold_expiry',
                    label: 'Hold Expiry',
                    visible: this.section === Section.open,
                    thStyle: { width: "9%" },
                    class: 'text-center',
                },
                {
                    key: 'actions',
                    label: 'Actions',
                    thStyle: { width: "11%" },
                    class: 'text-center',
                },
            ];
            return _.filter(fields, field => {
                return _.get(field, 'visible', true)
            })
        },
    },
    methods: {
        refresh() {
            this.getData({
                page: _.get(this.deals, 'meta.pagination.current_page')
            })
        },
        getData({page = 1}) {
            this.deals.loading = true;
            let query = Deal.page(page)
                .where(this.section, true)
                .include(
                    'vehicle.vehicleYear',
                    'vehicle.vehicleMake',
                    'vehicle.vehicleModel',
                    'vehicle.vehicleTrim',
                    'vehicle.company',
                    'vehicle.images',
                    'buyer',
                    'seller',
                    'buyer.companies',
                    'seller.companies',
                );

            query.where('deal_type', this.deal_type);

            query.get()
                .then(response => {
                    this.deals.data = _.map(response.data, deal => {
                        deal.is_paid = !!deal.paid_date;
                        deal.is_delivered = !!deal.delivered_date;
                        return deal;
                    });
                    this.deals.meta = response.meta;
                })
                .finally(() => this.deals.loading = false);
        },
        onPageChange(page) {
            this.getData({page})
        },
        onCancelBuyPrice(deal){
            this.buy_price_form.amount = null;
            this.$refs[`buyPriceModal-${deal.id}`].hide();
            deal.is_paid = false;
        },
        confirmBuyPrice(deal) {
            this.buy_price_form.validate()
                .then(() => {
                    this.addBusyItem(deal, CustomActions.markPaid);
                    axios.post(`/web-api/deal/${deal.id}/paid`, this.buy_price_form)
                        .then(response => {
                            this.$notify.success({
                                message: `The deal has been marked as paid.`
                            });
                            _.extend(deal, response.data);
                            this.checkCompleted(response.data);
                            this.toggle_paid = deal.is_paid;
                            this.buy_price_form.amount = null;
                            this.$refs[`buyPriceModal-${deal.id}`].hide()
                        })
                        .catch(() => {
                            deal.is_paid = false;
                        })
                        .finally(() => this.removeBusyItem(deal, CustomActions.markPaid));
                })
        },
        onChangePaid(is_paid, deal) {
            if (is_paid) {
                this.$refs[`buyPriceModal-${deal.id}`].show();
                return;
            }

            bootbox.confirm({
                title: `Confirm Action`,
                centerVertical: true,
                message: 'Are you sure you want to unmark deal as paid?',
                buttons: {
                    confirm: {
                        label: 'Yes',
                    }
                },
                callback: (result) => {
                    if (!result) {
                        deal.is_paid = !is_paid;
                        return;
                    }
                    this.addBusyItem(deal, CustomActions.markPaid);
                    axios.post(`/web-api/deal/${deal.id}/paid`, {
                        is_paid,
                    })
                        .then(response => {
                            this.$notify.success({
                                message: `The deal has been unmarked as paid.`
                            });
                            _.extend(deal, response.data);
                            this.checkCompleted(response.data);
                            this.toggle_paid = deal.is_paid;
                        })
                        .catch(() => {
                            deal.is_paid = !is_paid;
                        })
                        .finally(() => this.removeBusyItem(deal, CustomActions.markPaid));
                },
            });
        },
        onChangeDelivered(is_delivered, deal) {
            bootbox.confirm({
                title: `Confirm Action`,
                centerVertical: true,
                message: `Are you sure you want to ${is_delivered ? '' : 'un'}mark deal as delivered?`,
                buttons: {
                    confirm: {
                        label: 'Yes',
                    }
                },
                callback: (result) => {
                    if (!result) {
                        deal.is_delivered = !is_delivered;
                        return;
                    }
                    this.addBusyItem(deal, CustomActions.markDelivered);
                    axios.post(`/web-api/deal/${deal.id}/delivered`, {
                        is_delivered,
                    })
                        .then(response => {
                            this.$notify.success({
                                message: `The deal has been ${is_delivered ? '' : 'un'}marked as delivered.`
                            });
                            _.extend(deal, response.data);
                            this.checkCompleted(response.data);
                        })
                        .catch(() => {
                            deal.is_delivered = !is_delivered;
                        })
                        .finally(() => this.removeBusyItem(deal, CustomActions.markDelivered));
                },
            });
        },
        checkCompleted(deal) {
            if (!!deal.paid_date && !!deal.delivered_date) {
                this.refresh();
                this.$emit('completed', deal)
            }
        },
        canSetOfferToPaidOrDelivered(deal) {
            let hasDealerOrManagerRole = AuthUtils.hasDealerOrManagerRole();

            if (DealStatus.completed.value === deal.vehicle.status) {
                return false;
            }

            if (!hasDealerOrManagerRole && deal.seller_id !== this.auth.user.id) {
                return false;
            }

            if (hasDealerOrManagerRole && this.auth.user.companies[0].id !== deal.vehicle.company_id) {
                return false;
            }

            return this.checkVehicleStatus(this.deals.data, deal);
        },
        checkVehicleStatus(deals, currDeal) {
            const vehicleDeals = deals.filter(deal => deal.vehicle_id === currDeal.vehicle_id);

            let statusOnHoldExists = false;

            for (const deal of vehicleDeals) {
                if (deal.status === DealStatus.onHold.value || deal.status === DealStatus.sold.value) {
                    statusOnHoldExists = true;
                }
            }

            if (vehicleDeals.length > 1 && statusOnHoldExists) {
                return false;
            }

            return true;
        },
        canArchiveDeal(deal){
            let hasDealerOrManagerRole = AuthUtils.hasDealerOrManagerRole();

            if (deal.vehicle.status !== DealStatus.completed.value) {
                return false;
            }

            if (deal.status === DealStatus.completed.value || deal.status === DealStatus.archived.value) {
                return false;
            }

            if (!hasDealerOrManagerRole && deal.seller_id !== this.auth.user.id) {
                return false;
            }

            if (hasDealerOrManagerRole && this.auth.user.companies[0].id !== deal.vehicle.company_id) {
                return false;
            }

            return true;
        },
        onArchiveDeal(deal) {
            bootbox.confirm({
                title: `Confirm Action`,
                centerVertical: true,
                message: `Are you sure you want to archive this deal?`,
                buttons: {
                    confirm: {
                        label: 'Yes',
                    }
                },
                callback: (result) => {
                    if (!result) {
                        return;
                    }
                    this.addBusyItem(deal, CustomActions.markDelivered);
                    axios.post(`/web-api/deal/${deal.id}/archive`)
                        .then(response => {
                            this.$notify.success({
                                message: `The deal has been archived.`
                            });
                            _.extend(deal, response.data);
                            this.refresh();
                            this.$emit('completed', response.data)
                        })
                        .finally(() => this.removeBusyItem(deal, CustomActions.markDelivered));
                },
            });
        },
        fetchVehiclesUnreadMessageCount() {
            this.messageCount = this.new_messages_count.length;
            axios.get(`/web-api/message/vehicle-unread-messages-count`)
                .then(response => {
                    this.new_messages_count = response.data;
                });
        },
        getVehicleUnreadMessageCountBubble(deal) {
            const vehicle_id = deal.vehicle_id;
            const deal_id = deal.id;
            let vehicle = _.find(this.new_messages_count, {vehicle_id, deal_id});
            if (!vehicle) {
                return '';
            }
            if (!vehicle.unread_count) {
                return '';
            }
            return `<span class="badge badge-pill badge-danger noti-icon-badge">${vehicle.unread_count}</span>`;
        },
        recipient(item) {
            let customerId = null;
            const { id } = this.auth.user;
            const authCompanyId = this.auth.user.companies[0].id

            if (item.seller === id || (item.seller.companies[0]?.id === authCompanyId)) {
                customerId = item.buyer_id;
            } else if (item.buyer === id || (item.buyer.companies[0]?.id === authCompanyId)) {
                customerId = item.seller_id;
            }

            return customerId;
        },
        handleAmountInput($event) {
            validateOnlyNumber($event)
        },
        startPolling() {
            this.pollInterval = setInterval(this.fetchVehiclesUnreadMessageCount, POLL_INTERVAL);
        },
        stopPolling() {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        },
        getName(vehicle) {
            return vehicle && vehicle.name ? vehicle.name : '';
        },
    },
    watch: {
        '$route.query.deal_type': function(newDealType) {
            this.deal_type = Number(newDealType);
        },
        deal_type: function(newDealType) {
            this.getData({});
        },
    },
}
</script>

<style>
.deals-thead {
  background-color: #15A9F0;
  color: #FFFFFF;
}
.action-checkbox {
  position: relative;
  display: inline-block;
  cursor: pointer;
}
.action-checkbox input {
  position: absolute;
  cursor: pointer;
  opacity: 0;
}
.noti-icon-badge {
    font-size: 10px;
}
.bubble-count-unread {
    top: -12px;
    right: -8px;
    font-style: normal;
}
.vehicle-image {
    width: 100px;
    height: 100px;
    object-fit: cover;
}
</style>
